/**
 * <collection-goal-grid>
 *
 * Client-side "Shop by goal" filtering + sorting for the collection grid.
 * Every product is rendered once; this element only shows/hides and
 * reorders the existing cards, so filtering costs no page load. Goals
 * are `goal` metaobject entries, identified by their handle: each card's
 * `data-goals` holds the handles from the product's `custom.goals`
 * metafield.
 *
 * The goals have two views that stay in sync — a checkbox sidebar on
 * desktop and a pill row on mobile. Sorting is desktop-only: on mobile it
 * only adds noise next to the pills, so the grid keeps its default sort.
 *
 * State is mirrored into the URL (?goal=…&sort=…) so a filtered view is
 * shareable and shows up as a distinct page path in analytics. The same
 * parameter is the deep link in: ?goal=<handle>, comma-separated for
 * several goals.
 */
class CollectionGoalGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-cgg-grid]');
    if (!this.grid) return;

    this.items = Array.from(this.grid.querySelectorAll('[data-cgg-item]'));
    this.checkboxes = Array.from(this.querySelectorAll('[data-cgg-goal]'));
    this.pills = Array.from(this.querySelectorAll('[data-cgg-goal-pill]'));
    this.pillsWrap = this.querySelector('[data-cgg-pills-wrap]');
    this.pillsScroller = this.querySelector('[data-cgg-pills]');
    this.select = this.querySelector('[data-cgg-sort]');
    this.emptyEl = this.querySelector('[data-cgg-empty]');

    // The goals this grid offers a control for. Anything else in `?goal=` is
    // ignored, so a stale or mistyped link can't filter down to nothing.
    this.knownGoals = new Set(
      [...this.checkboxes.map((input) => input.value), ...this.pills.map((pill) => pill.dataset.goal)].filter(Boolean)
    );

    this.defaultSort = this.dataset.defaultSort || 'popularity';
    this.syncUrl = this.dataset.syncUrl !== 'false';

    this.activeGoals = new Set();
    this.sort = this.defaultSort;

    this.readStateFromUrl();
    this.bindFilters();
    this.bindStickyOffset();
    this.bindPillScroll();

    window.addEventListener('popstate', () => {
      this.readStateFromUrl();
      this.apply({ animate: true, pushUrl: false });
    });

    this.apply({ animate: false, pushUrl: false });
  }

  /* ---------------- wiring ---------------- */

  bindFilters() {
    this.checkboxes.forEach((input) => {
      input.addEventListener('change', () => {
        const goal = input.value;
        if (input.checked) {
          this.activeGoals.add(goal);
        } else {
          this.activeGoals.delete(goal);
        }
        this.apply();
        this.track('filter');
      });
    });

    // The mobile pill row is a second view on the same goals as the sidebar
    // checkboxes — an empty data-goal is the "all" pill, which clears them.
    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const goal = pill.dataset.goal;
        if (!goal) {
          this.activeGoals.clear();
        } else if (this.activeGoals.has(goal)) {
          this.activeGoals.delete(goal);
        } else {
          this.activeGoals.add(goal);
        }
        this.apply();
        this.track('filter');
      });
    });

    if (this.select) {
      this.select.addEventListener('change', () => {
        this.sort = this.select.value;
        this.apply();
        this.track('sort');
      });
    }

    this.querySelectorAll('[data-cgg-reset]').forEach((el) => {
      el.addEventListener('click', () => {
        this.activeGoals.clear();
        this.apply();
        this.track('reset');
      });
    });
  }

  /**
   * The sticky sidebar has to clear the theme's navigation, which reveals
   * itself again on scroll up. Its lowest edge is the announcement bar
   * (`.utility-bar`) plus the header (`.section-header`, sticky right below
   * it) — measure both rather than guessing, so a changed announcement bar
   * or a taller header can't push the "Shop op doel" heading out of sight.
   */
  bindStickyOffset() {
    const setOffset = () => {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const bar = document.querySelector('.utility-bar');
      const offset = header.offsetHeight + (bar ? bar.offsetHeight : 0) + 16;
      this.style.setProperty('--cgg-sticky-top', `${Math.round(offset)}px`);
    };

    setOffset();
    window.addEventListener('resize', setOffset, { passive: true });
    window.addEventListener('load', setOffset, { once: true });
  }

  /**
   * The pill row fades out against the right viewport edge to show there is
   * more to scroll to. Drop the fade once the row is scrolled to its end —
   * which also covers the case where everything already fits.
   */
  bindPillScroll() {
    if (!this.pillsScroller || !this.pillsWrap) return;

    const update = () => {
      const el = this.pillsScroller;
      const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth - 1;
      this.pillsWrap.dataset.atEnd = atEnd ? 'true' : 'false';
    };

    this.pillsScroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('load', update, { once: true });
    update();
  }

  /* ---------------- state ---------------- */

  readStateFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const goals = (params.get('goal') || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter((value) => this.knownGoals.has(value));
    this.activeGoals = new Set(goals);

    const sort = params.get('sort');
    const allowed = this.select ? Array.from(this.select.options).map((option) => option.value) : [];
    this.sort = sort && allowed.includes(sort) ? sort : this.defaultSort;
  }

  writeStateToUrl() {
    if (!this.syncUrl) return;

    const params = new URLSearchParams(window.location.search);

    if (this.activeGoals.size) {
      params.set('goal', Array.from(this.activeGoals).join(','));
    } else {
      params.delete('goal');
    }

    if (this.sort && this.sort !== this.defaultSort) {
      params.set('sort', this.sort);
    } else {
      params.delete('sort');
    }

    const query = params.toString();
    const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  }

  /* ---------------- render ---------------- */

  apply({ animate = true, pushUrl = true } = {}) {
    const active = this.activeGoals;
    let visible = 0;

    this.items.forEach((item) => {
      const goals = (item.dataset.goals || '').split(' ').filter(Boolean);
      // Multi-select is OR: a product matches when it carries any active goal.
      const matches = active.size === 0 || goals.some((goal) => active.has(goal));
      item.hidden = !matches;
      if (matches) visible += 1;
    });

    this.sortItems();
    this.syncControls();

    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;

    if (animate && visible > 0) {
      this.grid.classList.remove('cgg__grid--fade');
      // Force a reflow so the animation restarts on every change.
      void this.grid.offsetWidth;
      this.grid.classList.add('cgg__grid--fade');
    }

    if (pushUrl) this.writeStateToUrl();
  }

  sortItems() {
    const comparators = {
      popularity: (a, b) => this.num(a, 'position') - this.num(b, 'position'),
      newest: (a, b) => this.num(b, 'published') - this.num(a, 'published'),
      'price-asc': (a, b) => this.num(a, 'price') - this.num(b, 'price'),
      'price-desc': (a, b) => this.num(b, 'price') - this.num(a, 'price'),
    };

    const comparator = comparators[this.sort] || comparators.popularity;
    const sorted = this.items.slice().sort(
      (a, b) =>
        // Groups never interleave: accessories and merchandise stay below the
        // supplements and bundles even when they'd sort higher on their own.
        this.num(a, 'group') - this.num(b, 'group') ||
        comparator(a, b) ||
        // Stable tiebreak on the original position keeps equal values in
        // merchandised order instead of shuffling between sorts.
        this.num(a, 'position') - this.num(b, 'position')
    );

    const fragment = document.createDocumentFragment();
    sorted.forEach((item) => fragment.appendChild(item));
    this.grid.appendChild(fragment);
  }

  /* Keep every control showing the current state — the sidebar checkboxes and
     the mobile pills are two views of the same set of goals. */
  syncControls() {
    this.checkboxes.forEach((input) => {
      input.checked = this.activeGoals.has(input.value);
    });

    this.pills.forEach((pill) => {
      const goal = pill.dataset.goal;
      const pressed = goal ? this.activeGoals.has(goal) : this.activeGoals.size === 0;
      pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      // Theme badge component: `.badge` at rest, `.badge-invert` when selected.
      pill.classList.toggle('badge-invert', pressed);
      pill.classList.toggle('badge', !pressed);
    });

    if (this.select) this.select.value = this.sort;
  }

  num(item, key) {
    return parseFloat(item.dataset[key]) || 0;
  }

  /* ---------------- analytics ---------------- */

  track(action) {
    const detail = {
      action,
      goals: Array.from(this.activeGoals),
      sort: this.sort,
      collection: this.dataset.collection || '',
    };

    this.dispatchEvent(new CustomEvent('collection:goal-grid', { bubbles: true, detail }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'collection_goal_grid', ...detail });
    }
  }
}

if (!customElements.get('collection-goal-grid')) {
  customElements.define('collection-goal-grid', CollectionGoalGrid);
}
